<template>
  <div>
    <div class="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto">
      <div class="mt-2 px-8">
        <div v-bind:class="{ 'border border-gray-400 py-3 rounded-md': !isResultView }" class="grid grid-cols-2 gap-4 px-2 mb-8">
          <h2 class="text-lg font-semibold text-gray-600">Test Results</h2>
          <div class="flex justify-end">
            <button @click="hideResultView" v-show="!isResultView"
                    class="px-4 py-1 text-custom-blue rounded hover:text-custom-yellow">
              Show All Results
            </button>
          </div>
        </div>
        <div v-show="isResultView">
          <table class="min-w-full divide-y divide-gray-200 border-x-2">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left font-medium text-gray-600">Imaging Type</th>
                <th scope="col" class="px-6 py-3 text-left font-medium text-gray-600">Reports</th>
                <th scope="col" class="px-6 py-3 text-left font-medium text-gray-600">Filled By</th>
                <th scope="col" class="px-6 py-3 text-left font-medium text-gray-600">Findings</th>
                <th scope="col" class="px-6 py-3 text-left font-medium text-gray-600">Recommendations</th>
                <th scope="col" class="px-6 py-3 text-left font-medium text-gray-600">Notes</th>
                <th scope="col" class="px-6 py-3 text-left font-medium text-gray-600">Status</th>
                <th scope="col" class="px-6 py-3 text-left font-medium text-gray-600"></th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(test, index) in filteredRequests" :key="index">
                <td class="px-6 py-2 whitespace-nowrap">{{ test.imaging_type?.name }}</td>
                <td class="px-6 py-2 whitespace-nowrap">
                  <span
                    v-for="(file, fileIndex) in test.radiology_results[0]?.url"
                    :key="fileIndex"
                    class="jiji_blue cursor-pointer block"
                    @click="downloadFile(file.url)"
                  >{{ file.filename || 'Download Report' }}</span>
                  <span v-if="!test.radiology_results[0]?.url || test.radiology_results[0]?.url.length === 0">No Attachment</span>
                </td>
                <td class="px-6 py-2 whitespace-nowrap">{{ test.radiology_results[0]?.filled_by?.Username }}</td>
                <td class="px-6 py-2 max-w-xs truncate" :title="test.radiology_results[0]?.findings">{{ test.radiology_results[0]?.findings || '—' }}</td>
                <td class="px-6 py-2 max-w-xs truncate" :title="test.radiology_results[0]?.recommendation">{{ test.radiology_results[0]?.recommendation || '—' }}</td>
                <td class="px-6 py-2 max-w-xs truncate" :title="test.radiology_results[0]?.notes">{{ test.radiology_results[0]?.notes || '—' }}</td>
                <td class="px-6 py-2 whitespace-nowrap">{{ test.status?.name }}</td>
                <td class="px-6 py-2 whitespace-nowrap">
                  <button
                    @click="showResultView(test)"
                    class="px-4 py-1 bg-custom-yellow text-white rounded hover:bg-yellow-600">
                    View
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="grid grid-cols-1 gap-6 mt-8">
            <div v-if="!reviewComplete" class="mb-0">
              <h3 class="text-md font-medium text-gray-600">Review Comments:</h3>
              <textarea v-model="reviewForm.comment" rows="3" placeholder="Type..."
                        class="w-full border-gray-300 rounded p-2 bg-gray-50 mt-2"></textarea>
            </div>
            <div class="mt-4 px-8 flex justify-end">
              <button
                v-if="!reviewComplete"
                :disabled="publishing"
                @click="submitReviewedResults"
                class="px-4 py-2 bg-custom-yellow text-white rounded hover:bg-yellow-600">
                {{ publishing ? "Reviewing..." : "Review & Publish" }}
              </button>
            </div>
          </div>
        </div>
        <div v-show="!isResultView" class="grid grid-cols-2 gap-6 mt-4">
          <div>
            <div class="mb-4">
              <h3 class="text-md font-medium text-gray-600 mt-2">Radiology Request ID:</h3>
              <p class="text-md mt-2">{{ selectedTest?.id || 'N/A' }}</p>
            </div>
            <div class="mb-4">
              <h3 class="text-md font-medium text-gray-600 mt-2">Patient ID:</h3>
              <p class="text-md mt-2">{{ selectedTest?.patient_id || 'N/A' }}</p>
            </div>
          </div>
          <div>
            <div class="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
              <h3 class="text-xs font-semibold text-gray-500 uppercase">Findings</h3>
              <p class="text-sm text-gray-800 mt-1 whitespace-pre-line">{{ selectedTest?.radiology_results[0]?.findings || 'N/A' }}</p>
            </div>
            <div v-if="selectedTest?.radiology_results[0]?.recommendation" class="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
              <h3 class="text-xs font-semibold text-gray-500 uppercase">Recommendation</h3>
              <p class="text-sm text-gray-800 mt-1 whitespace-pre-line">{{ selectedTest.radiology_results[0].recommendation }}</p>
            </div>
            <div v-if="selectedTest?.radiology_results[0]?.conclusion" class="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
              <h3 class="text-xs font-semibold text-gray-500 uppercase">Conclusion</h3>
              <p class="text-sm text-gray-800 mt-1 whitespace-pre-line">{{ selectedTest.radiology_results[0].conclusion }}</p>
            </div>
            <div class="mb-4">
              <h3 class="text-md font-medium text-gray-600 mt-2">Date & Time reviewed and published:</h3>
              <p class="text-md mt-2">{{ formattedTimestamp(selectedTest?.updated_at) || 'N/A' }}</p>
            </div>
            <div class="mb-4">
              <h3 class="text-md font-medium text-gray-600 mt-2">Reviewer/Radiologist:</h3>
              <p class="text-md">{{ selectedTest?.radiology_results[0]?.filled_by?.Username || 'N/A' }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";
import axios from "axios";

export default {
  name: 'ReviewRadiologyResultsTab',
  props: {
    requests: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isResultView: true,
      publishing: false,
      reviewComplete: false,
      reviewForm: {
        comment: ''
      },
      selectedTest: null,
      expandedFindingRows: [],
      expandedRecommendationRows: []
    };
  },
  watch: {
    requests: {
      immediate: true,
      handler(requests) {
        const totalItems = requests.length;
        const publishedItems = requests.filter(item => item.status?.code === "ST-013").length;
        this.reviewComplete = totalItems === publishedItems;
      }
    }
  },
  mounted() {
    this.$emit("mounted", "ReviewResultsTab");
  },
  computed: {
    filteredRequests() {
      return this.requests.filter(item => item.status?.code === "ST-003" || item.status?.code === "ST-013");
    }
  },
  methods: {
    toggleAccordion(testId, type) {
      if (type === 'findings') {
        if (this.isExpanded(testId, 'findings')) {
          this.expandedFindingRows = this.expandedFindingRows.filter(id => id !== testId);
        } else {
          this.expandedFindingRows.push(testId);
        }
      } else if (type === 'recommendations') {
        if (this.isExpanded(testId, 'recommendations')) {
          this.expandedRecommendationRows = this.expandedRecommendationRows.filter(id => id !== testId);
        } else {
          this.expandedRecommendationRows.push(testId);
        }
      }
    },
    isExpanded(testId, type) {
      if (type === 'findings') {
        return this.expandedFindingRows.includes(testId);
      } else if (type === 'recommendations') {
        return this.expandedRecommendationRows.includes(testId);
      }
    },
    showResultView(test) {
      this.isResultView = false;
      this.selectedTest = test;
    },
    hideResultView() {
      this.isResultView = true;
      this.selectedTest = null;
    },
    submitLabPayload() {
      const payload = {
        ids: this.extractIds(),
        ...this.reviewForm
      };
      return payload;
    },
    async submitReviewedResults() {
      this.publishing = true;
      const payload = this.submitLabPayload();
      try {
        const response = await apiClient.reviewRadiologyResults(payload);
        if (response.success) {
          this.publishing = false;
          this.$emit("refetch-requests");
          this.$toast.success("Radiology results reviewed and published successfully");
        } else {
          this.publishing = false;
          this.$toast.error("Failed to submit radiology results");
        }
      } catch (error) {
        this.publishing = false;
        this.$toast.error("Failed to submit radiology results: " + error.message);
        console.error("Submission error:", error);
      }
    },
    extractIds() {
      const items = this.requests.filter(item => item.status?.code === "ST-003");
      return items.map(item => item.id);
    },
    formattedTimestamp(dateString) {
      const options = {
        weekday: 'long', // "Friday", "Monday"
        year: "numeric", // "2025"
        month: "long", // "January"
        day: "numeric", // "22"
        hour: "numeric", // "1 PM"
        minute: "numeric", // "43"
        hour12: true, // 12-hour time format
      };

      // parse as UTC
      const utcDate = new Date(dateString);

      const date = new Date(utcDate.getTime());

      return date.toLocaleString("en-US", options);
    },
    async downloadFile(fileUrl) {
      console.log("Downloading file:", fileUrl);
      try {
        const response = await axios.get(fileUrl, { responseType: "blob" });
        let fileName = fileUrl.split("/").pop().split("?")[0];
        const contentDisposition = response.headers["content-disposition"];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match) fileName = match[1];
        }
        const blob = new Blob([response.data]);
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading file:", error);
        this.$toast.error("Failed to download file.");
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>